import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  Plug,
  Settings,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  Plus,
  ChevronRight,
  AlertTriangle,
  Play,
} from 'lucide-react'
import PortalLogo from '../../components/integracoes/PortalLogo'
import { allPortals as portalsData } from '../../constants/integracoes'
import { StatCard, Panel, Badge, Button, buttonClass } from '../../design-system'

interface PortalConfig {
  id: number
  portal: string
  nomeExibicao: string | null
  ativo: boolean
  autoPublicar: boolean
  autoRemoverVendido: boolean
  usarPrecoPromocional: boolean
  xmlFeedUrl: string | null
  xmlFeedAtivo: boolean
  ultimoTeste: string | null
  statusConexao: string
  ultimoErro: string | null
}

// Usando allPortals centralizado importado como portalsData

export default function PlataformasList() {
  const { showApiError, showSuccess } = useNotification()
  const [loading, setLoading] = useState(true)
  const [portaisConfigurados, setPortaisConfigurados] = useState<PortalConfig[]>([])
  const [testando, setTestando] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    fetchPortais(controller.signal)
    return () => controller.abort()
  }, [])

  const fetchPortais = async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      const response = await api.get('/portais', { signal })
      setPortaisConfigurados(response.data)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        showApiError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  const testarConexao = async (portal: string) => {
    try {
      setTestando(portal)
      const response = await api.post(`/portais/${portal}/testar`)
      if (response.data.sucesso) {
        showSuccess(`${portal}: ${response.data.message || 'Conexão bem sucedida'}`)
      } else {
        showApiError({ message: `${portal}: ${response.data.message || 'Falha na conexão'}` })
      }
      fetchPortais()
    } catch (error) {
      showApiError(error)
    } finally {
      setTestando(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONECTADO':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />
      case 'ERRO':
        return <XCircle className="w-5 h-5 text-rose-500" />
      default:
        return <Clock className="w-5 h-5 text-slate-400" />
    }
  }

  const getPortalConfig = (portalId: string): PortalConfig | undefined => {
    return portaisConfigurados.find(p => p.portal === portalId)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900">
            <Plug className="w-7 h-7 text-primary-600" />
            Plataformas de Integração
          </h1>
          <p className="mt-1 text-slate-500">
            Configure suas integrações com marketplaces de veículos
          </p>
        </div>
        <Button variant="secondary" onClick={() => fetchPortais()}>
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Conectadas"
          value={portaisConfigurados.filter(p => p.statusConexao === 'CONECTADO').length}
          tone="positive"
          icon={<CheckCircle2 className="h-[18px] w-[18px]" />}
          delay={0}
        />
        <StatCard
          title="Não testadas"
          value={portaisConfigurados.filter(p => p.statusConexao === 'NAO_TESTADO').length}
          icon={<Clock className="h-[18px] w-[18px]" />}
          delay={60}
        />
        <StatCard
          title="Com erro"
          value={portaisConfigurados.filter(p => p.statusConexao === 'ERRO').length}
          tone={portaisConfigurados.filter(p => p.statusConexao === 'ERRO').length > 0 ? 'negative' : 'default'}
          icon={<XCircle className="h-[18px] w-[18px]" />}
          delay={120}
        />
      </div>

      {/* Lista de Plataformas */}
      <Panel title="Todas as Plataformas" bodyClassName="!p-0">
        <div className="divide-y divide-slate-100">
          {portalsData.map((portal) => {
            const config = getPortalConfig(portal.id)
            const isConfigurado = !!config
            const isAtivo = config?.ativo ?? false
            const status = config?.statusConexao ?? 'NAO_CONFIGURADO'

            return (
              <div key={portal.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <PortalLogo portal={portal.id} size="lg" />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-900">{portal.nome}</h3>
                        {!isConfigurado && (
                          <Badge tone="neutral">Não configurado</Badge>
                        )}
                        {isConfigurado && !isAtivo && (
                          <Badge tone="warning">Desativado</Badge>
                        )}
                        {isConfigurado && isAtivo && (
                          <Badge tone="success">Ativo</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{portal.descricao}</p>
                      {config?.ultimoErro && status === 'ERRO' && (
                        <p className="text-xs text-rose-500 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {config.ultimoErro}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Status */}
                    <div className="flex items-center gap-2">
                      {isConfigurado ? getStatusIcon(status) : (
                        <Plus className="w-5 h-5 text-slate-400" />
                      )}
                    </div>

                    {/* Botão Testar */}
                    {isConfigurado && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => testarConexao(portal.id)}
                        disabled={testando === portal.id}
                        title="Testar conexão"
                      >
                        {testando === portal.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                        Testar
                      </Button>
                    )}

                    {/* Botão Configurar */}
                    <Link
                      to={`/integracoes/plataformas/${portal.id.toLowerCase()}`}
                      className={buttonClass('primary', 'sm')}
                    >
                      <Settings className="w-4 h-4" />
                      {isConfigurado ? 'Configurar' : 'Adicionar'}
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Detalhes extras quando configurado */}
                {isConfigurado && (
                  <div className="mt-3 ml-16 flex items-center gap-6 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      Auto-publicar: {config.autoPublicar ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <XCircle className="w-3 h-3 text-slate-400" />
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      Auto-remover vendido: {config.autoRemoverVendido ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <XCircle className="w-3 h-3 text-slate-400" />
                      )}
                    </span>
                    {config.ultimoTeste && (
                      <span>
                        Último teste: {new Date(config.ultimoTeste).toLocaleString('pt-BR')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </Panel>
    </div>
  )
}
